// Copyright (c) 2026. Hardfin, Inc. All rights reserved.
// Hardfin Confidential - Restricted
import { api } from '@/api/axios-instance';
import { parseResponse } from '@/api/parse-response';
import { responseMetadataSchema } from '@/api/types';
import { CostFilter } from '@/costs/api/get-costs';
import { CostTimeSeries, costTimeSeriesSchema } from '@/costs/api/types';
import { z } from 'zod';

export interface GetCostTimeSeriesParams {
  filter?: Omit<CostFilter, 'pageSize' | 'pageNumber' | 'sort'>;
  interval?: 'daily' | 'weekly' | 'monthly' | 'quarterly' | 'yearly';
  groupBy?: 'person' | 'project' | 'category';
}

/**
 * Response schema for cost time series API
 */
const costTimeSeriesResponseSchema = z.object({
  data: costTimeSeriesSchema,
  metadata: responseMetadataSchema,
});

/**
 * Fetches cost time series data from the API
 */
export const getCostTimeSeries = async (
  params: GetCostTimeSeriesParams = {}
): Promise<CostTimeSeries> => {
  const searchParams = new URLSearchParams();

  // Add filter parameters
  const filter = params.filter;
  if (filter) {
    // Add date range parameters
    if (filter.startDate) {
      searchParams.append('startDate', filter.startDate);
    }
    if (filter.endDate) {
      searchParams.append('endDate', filter.endDate);
    }

    // Add search parameter
    if (filter.search) {
      searchParams.append('search', filter.search);
    }

    // Add person IDs
    if (filter.personIds && filter.personIds.length > 0) {
      filter.personIds.forEach((id) => {
        searchParams.append('personId', id);
      });
    }

    // Add project IDs
    if (filter.projectIds && filter.projectIds.length > 0) {
      filter.projectIds.forEach((id) => {
        searchParams.append('projectId', id);
      });
    }

    // Add categories
    if (filter.categories && filter.categories.length > 0) {
      filter.categories.forEach((cat) => {
        searchParams.append('category', cat);
      });
    }
  }

  // Add interval parameter (default to daily)
  searchParams.append('interval', params.interval || 'daily');

  // Add groupBy parameter if provided
  if (params.groupBy) {
    searchParams.append('groupBy', params.groupBy);
  }

  const url = `/v1/cost-aggregate/granular?${searchParams.toString()}`;
  const response = await api.get(url);

  // Parse the full response and return just the data part
  const parsedResponse = await parseResponse(
    costTimeSeriesResponseSchema,
    response.data,
    {
      schemaName: 'CostTimeSeries',
      endpoint: url,
    }
  );
  return parsedResponse.data;
};
